import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {Observable} from 'rxjs';
import {
    APIResponse,
    Filter,
    FilterFieldOption,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    PagingResponse,
    PresignedURL,
} from '@core/interfaces/system/system-common';
import {FormResponseDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {Document, DocumentCreate} from '@core/interfaces/common/document';
import {Dataset} from '@core/interfaces/common/dataset';

export interface Form {
    assetNumber: number;
    createdDate: string;
    description: string;
    formCode: string;
    formVersion: number;
    id: number;
    name: string;
    status: FormStatusType;
    userName: string;
    configured: boolean;
}
export interface FormScope {
    id: number;
    createdDate: string;
    userName: string;
    name: string;
    formCode: string;
    description: string;
    assetNumber: string | null;
    status: FormStatusType;
    configured: boolean;
}
export interface DocumentScope {
    id: number;
    createdOn: string;
    updatedOn: string;
    fileFormat: string;
    fileName: string;
    fileKey: string;
    bucketName: string;
    fileSize: number;
    status: string;
    description: string;
    formNumber: number;
    userId: string;
    userName: string;
    url: string;
}
export enum FormStatusType {
    PUBLISHED = 'PUBLISHED',
    READY_FOR_PUBLISH = 'READY_FOR_PUBLISH',
    IN_PROCESS = 'IN_PROCESS',
    DELETED = 'DELETED',
}
export interface DocumentUpdateDto {
    fileName: string;
    description: string;
}
export interface DocumentFormScope {
    formId: number;
    id: number;
    inspectionDocumentId: number;
}

export const FormStatusTypeLabels = {
    [FormStatusType.PUBLISHED]: 'Published',
    [FormStatusType.READY_FOR_PUBLISH]: 'Ready for publish',
    [FormStatusType.IN_PROCESS]: 'In progress',
};

export abstract class FormsService {
    // Form content: basic content
    abstract createForm(name: string, description: string): Observable<APIResponse<Form>>;

    abstract toggleFormPublish(formId: number, isPublished: boolean): Observable<APIResponse<Form>>;

    abstract updateForm(formId: number, name: string, description: string): Observable<APIResponse<Form>>;

    abstract getInspectionFormDto(formId: number): Observable<APIResponse<FormResponseDto>>;

    // Form content: scope
    abstract getFormScope(formId: number): Observable<APIResponse<Filter[]>>;

    abstract setFormScope(formId: number, filters: Filter[]): Observable<APIResponse<any>>;

    abstract getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>>;

    // Form content: config
    abstract commitFormConfig(
        formId: number,
        isConfirmed: boolean,
        dataPrepToolId: number,
    ): Observable<APIResponse<Form>>;

    abstract downloadFormConfig(formId: number): Observable<APIResponse<DownloadCSVUrl[]>>;

    // Other
    abstract getInspectionFormsDataSource(): Observable<DataSource>;
    abstract getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormScope>>;
    abstract getInspectionForms(pageSize?: number): Observable<Form[]>;

    abstract duplicateForm(formId: number): Observable<APIResponse<any>>;

    abstract deleteForm(formId: number): Observable<APIResponse<any>>;

    abstract downloadInspectionForms(): Observable<APIResponse<DownloadCSVUrl>>;
    abstract updateDocumentScopeByFormId(
        formIds: number,
        params: {inspectionDocumentIds: number[]},
    ): Observable<APIResponse<Document>>;

    // Document management
    abstract listFiles(): Observable<DataSource>;

    /** This gets the same content as listFiles, but an extra property `selected` for the given formId */
    abstract listFilesByFormId(formId: number): Observable<DataSource>;

    abstract listSelectedFiles(selectedId: number[]): Observable<PagingResponse<DocumentScope>>;
    abstract create(document: DocumentCreate): Observable<APIResponse<Document>>;
    abstract update(documentId: number, document: DocumentUpdateDto): Observable<APIResponse<Document>>;
    abstract deleteDocument(documentId: number): Observable<APIResponse<boolean>>;
    abstract updateDocumentScope(documentId: number, params: {formIds: number[]}): Observable<APIResponse<Document>>;
    abstract getSelectedFormsByDocumentId(
        documentId: number,
    ): Observable<APIResponse<PagingResponse<DocumentFormScope>>>;
    abstract getSelectedDocumentByFormsId(formId: number): Observable<APIResponse<PagingResponse<DocumentFormScope>>>;
    abstract getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>>;
    abstract getDownloadUrl(documentId: number): Observable<string>;
    abstract uploadToPresignedUrl(url: string, data: File);
    abstract getFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
    ): Observable<APIResponse<FilterFieldOptionResponse>>;
}
